import { createSlice } from '@reduxjs/toolkit';
import { showLoading, hideLoading } from 'react-redux-loading-bar';
import { addDays, parseISO, startOfDay } from 'date-fns';
import { getOrderGroups, searchOrderGroups, getReportByOrderGroupStatus, updateOrderGroup, deleteOrderGroup } from './orderGroupAPI';

export const orderGroupSlice = createSlice({
  name: 'orderGroup',
  initialState: {
    orderGroups: [],
    status: 'idle',
    editOrderGroupPaymentStatusModalOpen: false,
    editOrderGroupModalOpen: false,
    deleteOrderGroupModalOpen: false,
    pagination: {},
    page: 0,
    rowsPerPage: localStorage.getItem("rowsPerPage") ? parseInt(localStorage.getItem("rowsPerPage")) : 10,
    selectedIds: []
  },
  reducers: {
    startFilteredRequest: (state, action) => {
      state.filterRequestId = action.payload;
      state.status = 'loading';
      state.orderGroups = [];
      state.pagination = {};
      state.selectedIds = [];
    },
    failFilteredRequest: (state) => {
      state.status = 'failed';
    },
    openEditOrderGroupModal: (state) => {
      state.editOrderGroupModalOpen = true;
    },
    closeEditOrderGroupModal: (state) => {
      state.editOrderGroupModalOpen = false;
    },
    openEditOrderGroupPaymentStatusModal: (state) => {
      state.editOrderGroupPaymentStatusModalOpen = true;
    },
    closeEditOrderGroupPaymentStatusModal: (state) => {
      state.editOrderGroupPaymentStatusModalOpen = false;
    },
    resetOrderGroupStatus: (state) => {
      state.status = 'idle';
    },
    openDeleteOrderGroupModal: (state) => {
      state.deleteOrderGroupModalOpen = true;
    },
    closeDeleteOrderGroupModal: (state) => {
      state.deleteOrderGroupModalOpen = false;
      state.selectedIds = [];
    },
    setSelectedIds: (state, action) => {
      state.selectedIds = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setOrderGroups: (state, action) => {
      state.orderGroups = action.payload.map(o => o.pickUpDateTime ? {...o, pickUpDateTime: new Date(o.pickUpDateTime).toString()} : o);
      state.status = 'success'
    },
  },
});

export const { openEditOrderGroupModal, closeEditOrderGroupModal, openEditOrderGroupPaymentStatusModal, closeEditOrderGroupPaymentStatusModal, resetOrderGroupStatus, openDeleteOrderGroupModal, closeDeleteOrderGroupModal, setSelectedIds, setPage, setRowsPerPage, setPagination, setOrderGroups } = orderGroupSlice.actions;

export const handleGetOrderGroups = (orderGroupType) => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  let status = orderGroupType === 'COMPLETED' ? 'SUCCESS,PARTIAL_SUCCESS' : 'CONFIRMED_PROCESSING,CONFIRMED,ASSIGNED,IN_TRANSIT'
  try {
    const response = await getOrderGroups(state.orderGroup.page, state.orderGroup.rowsPerPage, status);
    const { content, ...pagination } = response.data;
    let orderGroups = content;

    dispatch(setPagination(pagination));
    switch(orderGroupType) {
      case 'OPEN':
        dispatch(setOrderGroups(orderGroups.filter(orderGroup => orderGroup.status === "OPEN")));
        break;
      case 'COMPLETED':
        dispatch(setOrderGroups(orderGroups.filter(orderGroup => orderGroup.status === "SUCCESS" || orderGroup.status === "PARTIAL_SUCCESS")));
        break;
      default:
        dispatch(setOrderGroups(orderGroups.filter(orderGroup => orderGroup.status !== "OPEN" && orderGroup.status !== "SUCCESS" && orderGroup.status !== "PARTIAL_SUCCESS")));
        break;
    }
  } catch (error) {
    console.log(error.message);
    alert(error.message)
  } finally {
    dispatch(hideLoading());
  }
};

let nextFilterRequestId = 0;

export const pickUpRange = (filters, now = new Date()) => {
  const today = startOfDay(now);
  switch (filters.datePreset) {
    case "today":
      return { from: today, to: addDays(today, 1) };
    case "tomorrow":
      return { from: addDays(today, 1), to: addDays(today, 2) };
    case "upcoming":
      return { from: today };
    case "overdue":
      return { to: now };
    case "custom":
      return {
        from: filters.fromDate ? parseISO(filters.fromDate) : undefined,
        to: filters.toDate ? addDays(parseISO(filters.toDate), 1) : undefined,
      };
    default:
      return {};
  }
};

export const toSearchParams = (filters, page, size, now = new Date()) => {
  const { from, to } = pickUpRange(filters, now);
  const params = new URLSearchParams({ page, size, direction: from ? "ASC" : "DESC" });
  if (filters.status.length) params.set("status", filters.status.join(","));
  if (filters.q.trim()) params.set("q", filters.q.trim());
  if (filters.paid) params.set("paid", filters.paid);
  if (filters.platform) params.set("platform", filters.platform);
  if (from) params.set("from", from.toISOString());
  if (to) params.set("to", to.toISOString());
  return params.toString();
};

export const handleGetOrderGroupsByFilter = (filters) => async (dispatch, getState) => {
  const requestId = ++nextFilterRequestId;
  dispatch(orderGroupSlice.actions.startFilteredRequest(requestId));
  dispatch(showLoading());
  const state = getState();

  try {
    const response = await searchOrderGroups(toSearchParams(filters, state.orderGroup.page, state.orderGroup.rowsPerPage));
    if (getState().orderGroup.filterRequestId !== requestId) return;
    const { content, ...pagination } = response.data;
    let orderGroups = content;

    dispatch(setPagination(pagination));
    dispatch(setOrderGroups(orderGroups));
  } catch (error) {
    if (getState().orderGroup.filterRequestId !== requestId) return;
    dispatch(orderGroupSlice.actions.failFilteredRequest());
    console.log(error.message);
    alert(error.message)
  } finally {
    dispatch(hideLoading());
  }
};

export const handleDownloadOrderGroups = (orderGroupType) => async (dispatch, getState) => {
  dispatch(showLoading());

  let status = orderGroupType === 'COMPLETED' ? 'SUCCESS,PARTIAL_SUCCESS' : 'CONFIRMED_PROCESSING,CONFIRMED,ASSIGNED,IN_TRANSIT'
  try {
    const response = await getReportByOrderGroupStatus(status);
    
    let report = await response.data;
    // 2. Create blob link to download
    const url = window.URL.createObjectURL(new Blob([report]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${orderGroupType}-ordergroup-report.csv`);
    // 3. Append to html page
    document.body.appendChild(link);
    // 4. Force download
    link.click();
    // 5. Clean up and remove the link
    link.parentNode.removeChild(link);
  } catch (error) {
    console.log(error.message);
  } finally {
    dispatch(hideLoading());
  }
};


export const handleDeleteOrderGroup = (id, orderGroupType) => async (dispatch) => {
  dispatch(showLoading());
  try {
    const response = await deleteOrderGroup(id);
    if(response.error) throw response.error;
    dispatch(closeDeleteOrderGroupModal());
    dispatch(handleGetOrderGroups(orderGroupType));
  } catch (error) {
    console.log(error.message);
    alert(error.message);
  } finally {
    dispatch(hideLoading());
  }
};

export const handleUpdateOrderGroup = (payload, id, orderGroupType) => async (dispatch) => {
  dispatch(showLoading());
  try {
    const response = await updateOrderGroup(payload, id);
    if(response.error) throw response.error;
    dispatch(closeEditOrderGroupModal());
    dispatch(handleGetOrderGroups(orderGroupType));
  } finally {
    dispatch(hideLoading());
  }
};

export const selectState = state => state.orderGroup;

export default orderGroupSlice.reducer;
